import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import MDBox from "components/MDBox";
import DashboardLayoutCustom2 from "examples/LayoutContainers/DashboardLayoutCustom2";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
  Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MDTypography from "components/MDTypography";
import Markdown from "react-markdown";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { getBusinessIntelligenceAsync } from "features/actions/auth";
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
const BITabs = () => {
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(null);
  const [businessId, setBusinessId] = useState(
    localStorage.getItem("common_id")
  );

  const { sections, loading, error } = useSelector(
    (state) => state.businessIntelligence
  );

  // store one ref per section
  const sectionRefs = useRef([]);

  useEffect(() => {
    if (businessId) {
      dispatch(getBusinessIntelligenceAsync(businessId));
    }
  }, [businessId, dispatch]);

  useEffect(() => {
    const interval = setInterval(() => {
      const newBusinessId = localStorage.getItem("common_id");
      if (newBusinessId !== businessId) {
        setBusinessId(newBusinessId);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [businessId]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : null);
  };

  return (
    <MDBox sx={{ backgroundColor: "#f9f9f9", minHeight: "100vh" }}>
      <DashboardLayoutCustom2>
        <DashboardNavbar />
        <MDBox
          sx={{
            margin: "15px",
            marginTop: "25px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            backgroundColor: "#ffffff",
            borderRadius: "16px",
            p: 3,
          }}
        >
          <MDTypography
            mt={2}
            mb={4}
            sx={{
              color: "#292929",
              fontFamily: "Urbanist",
              fontSize: "24px",
              fontWeight: 700,
            }}
          >
            Business Intelligence
            <Box
              sx={{
                display: "flex",
                justifyContent: "start",
                gap: "5px",
                alignItems: "center",
                fontSize: "15px",
                fontWeight: "500",
                color: "#1565C0",
              }}
            >
              <Box component="span">
                Customized Business Intelligence based on your customer reviews and business data.
              </Box>

              <Box sx={{ flexGrow: 1 }} /> {/* Spacer pushes date to right */}

              {sections?.[0]?.date && (
                <Box component="span" sx={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  {sections[0].date}
                </Box>
              )}
            </Box>
          </MDTypography>

          {loading ? (
            <MDBox
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress />
            </MDBox>
          ) : error ? (
            <Typography color="error" textAlign="center">
              {error}
            </Typography>
          ) : (
            sections.map((section, index) => {
              // Initialize a ref for each section
              if (!sectionRefs.current[index]) {
                sectionRefs.current[index] = React.createRef();
              }

              const handleExport = async () => {
                const input = sectionRefs.current[index].current;
                if (!input) return;

                // Hide the export button before capturing
                const exportButton = input.querySelector(".export-button");
                if (exportButton) exportButton.style.display = "none";

                const canvas = await html2canvas(input, {
                  scale: 2,
                  useCORS: true,
                  backgroundColor: "#ffffff",
                  windowWidth: input.scrollWidth,
                  windowHeight: input.scrollHeight,
                });

                // Show the export button again
                if (exportButton) exportButton.style.display = "inline-block";

                const imgData = canvas.toDataURL("image/png");

                const pdf = new jsPDF("p", "mm", "a4");
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();

                const imgProps = pdf.getImageProperties(imgData);
                const imgWidth = pageWidth - 20; // 10mm padding on each side
                const imgHeight = (imgProps.height * imgWidth) / imgProps.width;

                const headerHeight = 17;
                const footerHeight = 15;
                const titleHeight = 10;
                const contentYStart = headerHeight + titleHeight + 5; // Add space for title

                const addHeaderAndFooter = () => {
                  // Header
                  const business_name = localStorage.getItem("business_name") || "";
                  pdf.setFillColor(17, 100, 209); // sky blue
                  pdf.rect(0, 0, pageWidth, headerHeight, "F");
                  pdf.setTextColor(255, 255, 255); // white

                  pdf.setFontSize(14);
                  pdf.text(
                    "TavloAI Business Intelligence",
                    pageWidth / 2,
                    8,
                    {
                      align: "center",
                    }
                  );

                  // Add business name below
                  if (business_name) {
                    pdf.setFontSize(12);
                    pdf.text(business_name, pageWidth / 2, 14, { align: "center" });
                  }

                  // Section Title on left
                  pdf.setTextColor(0, 0, 0); // black
                  pdf.setFontSize(12);
                  pdf.text(section.title, 10, headerHeight + 7); // Left-aligned

                  // Section Title (left) and Date (right)
                  pdf.setTextColor(0, 0, 0); // black
                  pdf.setFontSize(12);
                  pdf.text(section.title, 10, headerHeight + 7); // Left-aligned
                  pdf.text(`Date: ${section.date}` || "", pageWidth - 10, headerHeight + 7, { align: "right" }); // Right-aligned


                  // Footer
                  pdf.setTextColor(150, 150, 150);
                  pdf.setFontSize(10);
                  pdf.text(
                    "All rights reserved.",
                    pageWidth / 2,
                    pageHeight - 7,
                    {
                      align: "center",
                    }
                  );
                };

                if (imgHeight > pageHeight - contentYStart - footerHeight) {
                  // Multi-page
                  let position = 0;
                  let pageIndex = 0;
                  let heightLeft = imgHeight;

                  while (heightLeft > 0) {
                    if (pageIndex > 0) pdf.addPage();
                    addHeaderAndFooter();
                    const xMargin = 15;
                    const imgWidth = pageWidth - xMargin * 2;

                    pdf.addImage(
                      imgData,
                      "PNG",
                      xMargin,
                      contentYStart,
                      imgWidth,
                      imgHeight,
                      undefined,
                      "FAST"
                    );
                    heightLeft -= pageHeight - contentYStart - footerHeight;
                    position += pageHeight;
                    pageIndex++;
                  }
                } else {
                  // Single page
                  addHeaderAndFooter();
                  const xMargin = 15;
                  const imgWidth = pageWidth - xMargin * 2;

                  pdf.addImage(
                    imgData,
                    "PNG",
                    xMargin,
                    contentYStart,
                    imgWidth,
                    imgHeight,
                    undefined,
                    "FAST"
                  );
                }

                pdf.save(`${section.title.replace(/\s+/g, "_")}.pdf`);
              };

              return (
                <Accordion
                  defaultExpanded
                  key={index}
                  // expanded={expanded === index}
                  onChange={handleChange(index)}
                  sx={{
                    mb: 2,
                    borderRadius: "12px !important",
                    boxShadow:
                      expanded === index
                        ? "0px 4px 8px rgba(0, 0, 0, 0.08)"
                        : "none",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": { backgroundColor: "#f5f5f5" },
                  }}
                >
                  {/* <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      backgroundColor: expanded === index ? "#e3f2fd" : "#e3f2fd",
                      borderRadius: "12px",
                      transition: "background 0.3s ease-in-out",
                      "&:hover": { backgroundColor: "#e3f2fd" },
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <MDBox
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        fontWeight={600}
                        sx={{ color: "#1565C0", fontSize: "18px" }}
                      >
                        {section.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#666",
                          marginLeft: "auto",
                        }}
                      >

                      </Typography>
                    </MDBox>
                  </AccordionSummary> */}

                  <AccordionDetails

                    sx={{
                      backgroundColor: "#f9f9f9",
                      borderRadius: "0 0 12px 12px",
                      position: "relative",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "#333",
                        fontSize: "16px",
                        lineHeight: "1.6",
                        textAlign: "left"
                      }}
                      padding={3}
                      className="markdown-body"
                      backgroundColor="transparent !important"
                      ref={sectionRefs.current[index]}
                    >
                      <Markdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {section.response}
                      </Markdown>
                    </Typography>

                    <Box sx={{ mt: 2, textAlign: "right" }}>
                      <button
                        onClick={handleExport}
                        className="export-button"
                        style={{
                          padding: "8px 16px",
                          backgroundColor: "#1565C0",
                          color: "white",
                          border: "none",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontWeight: "500",
                          fontSize: "14px",
                        }}
                      >
                        Export to PDF
                      </button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })
          )}
        </MDBox>
      </DashboardLayoutCustom2>
    </MDBox>
  );
};

export default BITabs;
